// @flow
import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import GeneralSettings from './GeneralSettings';
// import ReturnPolicy from './ReturnPolicy';
import {usePage} from '../../../page/utils';

import type {
  Contact,
  ContactType,
  StoreInformation,
  StoreProfileInformation,
  Store,
  StoreType,
  Country,
  User,
} from '../../../../entities';
import type {
  SaveGeneralContactRequest,
  SaveGeneralInformationRequest,
  SaveStoreProfileInformationRequest,
  SaveGeneralSettingsRequest,
} from '../api';
import queryString from 'query-string';
import {useHistory} from 'react-router-dom';

import type {Domain} from '../../../../entities';

type Props = {|
  saveGeneralSettings: ({dataGeneral: SaveGeneralSettingsRequest}) => void,
  saveGeneralInformation: ({
    dataInformation: SaveGeneralInformationRequest,
  }) => void,
  resetState: () => void,
  store?: Store,
  user?: User,
  currency?: Object,
  setCurrency: ({currencyId: string}) => void,
  setCurrencySuccess?: boolean,
  countries?: Country[],
  country: Country,
  fetchCountryError?: string,
  fetchCountries: () => void,
  saveStoreCountry: ({countryId: string}) => void,
  isSaving?: boolean,
  error?: string,
  savingSuccess?: boolean,
  information?: StoreInformation,
  isFetchingInformation?: boolean,
  storeProfileInformation?: StoreProfileInformation,
  isFetchingStoreProfileInformation?: boolean,
  fetchStoreProfileInformation: () => void,
  saveStoreProfileInformation: ({
    storeProfileInformation: SaveStoreProfileInformationRequest,
  }) => void,
  contact?: Contact[],
  isFetchingContact?: boolean,
  contactTypes?: ContactType[],
  saveGeneralContact: ({dataContact: SaveGeneralContactRequest[]}) => void,
  onBoardingError?: string,
  isSettingConfig?: boolean,
  storeTypes?: StoreType[],
  getStoreTypes: () => void,
  isGettingStoreTypes?: boolean,
  validateDomain: ({subdomain: string}) => void,
  validationResult?: boolean,
  isValidating?: boolean,
  getDomainChangeToken: () => void,
  domainChangeToken: string,

  isValidatingSsl?: boolean,
  sslIsValid?: boolean,
  errorValidatingSsl?: string,
  isGeneratingSsl: boolean,
  startGeneratingSsl?: boolean,
  errorGeneratingSsl?: string,
  validateSsl: ({domain: string}) => void,
  generateSsl: ({domain: string}) => void,
  customDomain?: Domain,
  getCustomDomain: ({storeId: string}) => void,
|};

export default usePage<Props>(General, 'General Configuration');

function General(props: Props) {
  const {
    store,
    currency,
    setCurrency,
    setCurrencySuccess,
    countries,
    fetchCountries,
    country,
    fetchCountryError,
    saveStoreCountry,
    resetState,
    error,
    savingSuccess,
    isSaving,
    saveGeneralSettings,
    fetchStoreProfileInformation,
    contact,
    contactTypes,
    saveGeneralContact,
    storeTypes,
    getStoreTypes,
  } = props;

  const query = queryString.parse(window.location.search);
  const history = useHistory();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => resetState(), []);

  useEffect(() => {
    getStoreTypes();
  }, [getStoreTypes]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    fetchStoreProfileInformation();
  }, [fetchStoreProfileInformation, getStoreTypes]);

  const handleSaveGeneralSettings = (dataGeneral: SaveGeneralSettingsRequest) =>
    saveGeneralSettings({dataGeneral});

  const handleSaveGeneralContact = (dataContact: SaveGeneralContactRequest[]) =>
    saveGeneralContact({dataContact});

  useEffect(() => {
    if (savingSuccess && query.fromOnboarding) history.push('/home');
  }, [history, query, savingSuccess]);

  return (
    <>
      <SnackBar mode="error" message={error} />
      <SnackBar
        mode="success"
        message={
          savingSuccess || setCurrencySuccess
            ? 'Se guardaron los cambios'
            : undefined
        }
      />
      {store && (
        <>
          <GeneralSettings
            store={store}
            country={country}
            fetchCountryError={fetchCountryError}
            currency={currency}
            setCurrency={setCurrency}
            countries={countries}
            saveStoreCountry={saveStoreCountry}
            storeTypes={storeTypes}
            handleSaveGeneralSettings={handleSaveGeneralSettings}
            contact={contact}
            contactTypes={contactTypes}
            handleSaveGeneralContact={handleSaveGeneralContact}
            isSaving={isSaving}
          />

          {/* <ReturnPolicy
            returnPolicy={''}
            handleSaveGeneralInformation={handleSaveGeneralInformation}
            isSaving={isSaving}
          /> */}
          {/* {!!user && user.type === 'vendor-user' && (
            <DomainConfiguration
              domain={getBasicDomain(store.domains)}
              saveGeneralSettings={saveGeneralSettings}
              validateDomain={validateDomain}
              validationResult={validationResult}
              isValidating={isValidating}
              getDomainChangeToken={getDomainChangeToken}
              domainChangeToken={domainChangeToken}
              isValidatingSsl={isValidatingSsl}
              sslIsValid={sslIsValid}
              isGeneratingSsl={isGeneratingSsl}
              errorGeneratingSsl={errorGeneratingSsl}
              errorValidatingSsl={errorValidatingSsl}
              validateSsl={validateSsl}
              generateSsl={generateSsl}
              startGeneratingSsl={startGeneratingSsl}
              customDomain={customDomain}
              getCustomDomain={getCustomDomain}
              storeId={store.id}
            />
          )} */}
        </>
      )}
    </>
  );
}

General.propTypes = {
  saveGeneralSettings: PropTypes.func.isRequired,
  saveGeneralInformation: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  store: PropTypes.object,
  user: PropTypes.object,
  currency: PropTypes.object,
  setCurrency: PropTypes.func.isRequired,
  setCurrencySuccess: PropTypes.bool,
  countries: PropTypes.array,
  country: PropTypes.object,
  fetchCountryError: PropTypes.string,
  fetchCountries: PropTypes.func.isRequired,
  saveStoreCountry: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  error: PropTypes.string,
  savingSuccess: PropTypes.bool,
  information: PropTypes.object,
  isFetchingInformation: PropTypes.bool,
  storeProfileInformation: PropTypes.object,
  isFetchingStoreProfileInformation: PropTypes.bool,
  fetchStoreProfileInformation: PropTypes.func,
  saveStoreProfileInformation: PropTypes.func.isRequired,
  contact: PropTypes.array,
  isFetchingContact: PropTypes.bool,
  contactTypes: PropTypes.array,
  saveGeneralContact: PropTypes.func.isRequired,
  onBoardingError: PropTypes.string,
  isSettingConfig: PropTypes.bool,
  storeTypes: PropTypes.array,
  isGettingStoreTypes: PropTypes.bool,
  validateDomain: PropTypes.func.isRequired,
  validationResult: PropTypes.bool,
  isValidating: PropTypes.bool,
  getDomainChangeToken: PropTypes.func,
  domainChangeToken: PropTypes.string,
  isValidatingSsl: PropTypes.bool,
  sslIsValid: PropTypes.bool,
  isGeneratingSsl: PropTypes.bool,
  startGeneratingSsl: PropTypes.bool,
  errorValidatingSsl: PropTypes.string,
  errorGeneratingSsl: PropTypes.string,
  validateSsl: PropTypes.func.isRequired,
  generateSsl: PropTypes.func.isRequired,
};
